'use client';

import { useMediaPlayer, type MediaMetadata } from '@/context/MediaContext';
import { motion } from 'motion/react';
import { useCallback, useEffect, useMemo } from 'react';
import { MediaVariant } from '../types';
import { useDictionary } from '@/context/DictionaryContext';

type MediaPlayerButtonProps = {
  media: string;
  variant: MediaVariant;
  metadata: MediaMetadata;
};

const MediaPlayerButton = ({ media, variant, metadata }: MediaPlayerButtonProps) => {

  const { t } = useDictionary();

  const {
    actions: {
      togglePlayPause,
      closePlayer,
      playMedia,
      setPlaybackState
    },
    state: {
      currentMedia,
    },
    selectors: {
      isPlaying,
    }
  } = useMediaPlayer();

  const isCurrentMediaPlaying = useMemo(
    () => currentMedia === media && isPlaying,
    [currentMedia, media, isPlaying]
  );

  const handlePlayPause = useCallback(() => {
    if (currentMedia === media) {
      setPlaybackState(isCurrentMediaPlaying ? 'paused' : 'playing');
      return;
    }

    if (isCurrentMediaPlaying) {
      setPlaybackState('paused');
      return;
    }

    closePlayer();

    playMedia(media, {
      id: metadata.id,
      type: variant,
      title: metadata.title,
      cover: metadata.cover,
    });

  }, [currentMedia, media, isCurrentMediaPlaying, playMedia, togglePlayPause, closePlayer, setPlaybackState]);

  if (variant === 'video' || variant === 'stream') {
    return (
      <TelevisionMediaPlayerButton
        isPlaying={isCurrentMediaPlaying}
        handlePlayPause={handlePlayPause}
      />
    );
  }

  if (variant === 'audio' || variant === 'radio') {
    return (
      <RadioMediaPlayerButton
        isPlaying={isCurrentMediaPlaying}
        handlePlayPause={handlePlayPause}
      />
    );
  }

  return (
    <ArticleMediaPlayerButton
      isPlaying={isCurrentMediaPlaying}
      label={isCurrentMediaPlaying ? t('media.article.pause') : t('media.article.listen')}
      ariaLabel={isCurrentMediaPlaying ? t('media.article.ariaPause') : t('media.article.ariaListen')}
      handlePlayPause={handlePlayPause}
    />
  );
};

const ArticleMediaPlayerButton = ({
  isPlaying,
  label,
  ariaLabel,
  handlePlayPause
}: {
  isPlaying: boolean;
  label: string;
  ariaLabel: string;
  handlePlayPause: () => void;
}) => {
  return (
    <motion.button
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px',
        gap: '8px',
        backgroundColor: 'var(--color-background-surface)',
        borderInlineStart: `4px solid ${isPlaying ? 'var(--color-blue-500)' : 'var(--color-border-tertiary)'}`,
        color: isPlaying ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
      }}
      onClick={handlePlayPause}
      aria-label={ariaLabel}
    >
      <>
        {isPlaying ?
          <svg viewBox="0 0 24 24" style={{ width: '16px', height: '16px', fill: 'var(--color-blue-500)' }}>
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
          </svg>
          :
          <svg viewBox="0 0 24 24" style={{ width: '16px', height: '16px', fill: 'var(--color-blue-500)' }}>
            <path d="M8 5v14l11-7z" />
          </svg>
        }
        <span style={{ fontSize: '0.875rem' }}>{label}</span>
      </>
    </motion.button >
  );
};

const TelevisionMediaPlayerButton = ({ isPlaying, handlePlayPause }: { isPlaying: boolean, handlePlayPause: () => void }) => {
  return (
    <motion.button
      onClick={handlePlayPause}
      aria-label={isPlaying ? "Pause video" : "Play video"}
    >
      <svg height="24" width="24" viewBox="0 0 16 16" style={{ color: 'var(--color-icon-inverse)' }}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1.5 2.5H14.5V12.5C14.5 13.0523 14.0523 13.5 13.5 13.5H2.5C1.94772 13.5 1.5 13.0523 1.5 12.5V2.5ZM0 1H1.5H14.5H16V2.5V12.5C16 13.8807 14.8807 15 13.5 15H2.5C1.11929 15 0 13.8807 0 12.5V2.5V1Z"
          fill="currentColor"
        />
        {isPlaying ? (
          <g>
            <rect x="5" y="5" width="2" height="6" fill="currentColor" />
            <rect x="9" y="5" width="2" height="6" fill="currentColor" />
          </g>
        ) : (
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.745 6.76445L9.2161 8L6.745 9.23555V6.76445ZM11.1056 7.55279C11.4741 7.73705 11.4741 8.26295 11.1056 8.44721L6.22361 10.8882C5.89116 11.0544 5.5 10.8127 5.5 10.441L5.5 5.55902C5.5 5.18733 5.89116 4.94558 6.22361 5.1118L11.1056 7.55279Z"
            fill="currentColor"
          />
        )}
      </svg>
    </motion.button>
  );
}

const RadioMediaPlayerButton = ({ isPlaying, handlePlayPause }: { isPlaying: boolean, handlePlayPause: () => void }) => {

  const buttonVariants = {
    idle: { scale: 1 },
    hover: {
      scale: 1.05,
      transition: { type: "spring" as const, stiffness: 400, damping: 30 }
    },
    tap: {
      scale: 0.95,
      transition: { type: "spring" as const, stiffness: 600, damping: 20 }
    }
  };

  const circleVariants = {
    playing: {
      fill: "#E50100",
      scale: 1.02,
      transition: {
        fill: { duration: 0.3, ease: "easeOut" as const },
        scale: { type: "spring" as const, stiffness: 400, damping: 25 }
      }
    },
    paused: {
      fill: "#1a1a1a",
      scale: 1,
      transition: {
        fill: { duration: 0.3, ease: "easeOut" as const },
        scale: { type: "spring" as const, stiffness: 400, damping: 25 }
      }
    }
  };

  const playIconVariants = {
    hidden: {
      opacity: 0,
      scale: 0.5,
      rotate: 45,
      transition: {
        opacity: { duration: 0.1, ease: "easeIn" as const },
        scale: { duration: 0.15, ease: "easeIn" as const },
        rotate: { duration: 0.15, ease: "easeIn" as const }
      }
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        opacity: { duration: 0.2, delay: 0.1, ease: "easeOut" as const },
        scale: { duration: 0.25, delay: 0.05, ease: "easeOut" as const },
        rotate: { duration: 0.25, delay: 0.05, ease: "easeOut" as const }
      }
    }
  };

  const pauseIconVariants = {
    hidden: {
      opacity: 0,
      scale: 0.5,
      rotate: -45,
      transition: {
        opacity: { duration: 0.1, ease: "easeIn" as const },
        scale: { duration: 0.15, ease: "easeIn" as const },
        rotate: { duration: 0.15, ease: "easeIn" as const }
      }
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        opacity: { duration: 0.2, delay: 0.1, ease: "easeOut" as const },
        scale: { duration: 0.25, delay: 0.05, ease: "easeOut" as const },
        rotate: { duration: 0.25, delay: 0.05, ease: "easeOut" as const }
      }
    }
  };

  const pauseRectVariants = {
    hidden: {
      scaleY: 0.6,
      opacity: 0.8
    },
    visible: (i: number) => ({
      scaleY: 1,
      opacity: 1,
      transition: {
        delay: i * 0.04,
        type: "spring" as const,
        stiffness: 500,
        damping: 25
      }
    })
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      placeSelf: 'center',
      alignItems: 'center',
      gap: '0.5rem'
    }}>
      <motion.button
        variants={buttonVariants}
        initial="idle"
        whileHover="hover"
        whileTap="tap"
        onClick={handlePlayPause}
        aria-label={isPlaying ? "Pause radio" : "Play radio"}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          outline: 'none',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}
        whileFocus={{
          boxShadow: '0 0 0 2px rgba(229, 1, 0, 0.5)',
        }}
      >
        <motion.svg
          width="56"
          height="56"
          viewBox="0 0 80 80"
          style={{ overflow: 'visible' }}
        >
          <motion.circle
            cx="40"
            cy="40"
            r="38"
            fill="none"
            stroke={isPlaying ? "#E50100" : "transparent"}
            strokeWidth="1"
            opacity={isPlaying ? 0.3 : 0}
            animate={{
              opacity: isPlaying ? [0.3, 0.1, 0.3] : 0,
              scale: isPlaying ? [1, 1.05, 1] : 1,
            }}
            transition={{
              duration: 2,
              repeat: isPlaying ? Infinity : 0,
              ease: "easeInOut"
            }}
          />
          <motion.circle
            cx="40"
            cy="40"
            r="36"
            variants={circleVariants}
            animate={isPlaying ? "playing" : "paused"}
            style={{
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
            }}
          />
          <motion.polygon
            points="32 52 54 40 32 28"
            fill="white"
            variants={playIconVariants}
            animate={isPlaying ? "hidden" : "visible"}
            style={{
              transformOrigin: '43px 40px',
              filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
            }}
          />
          <motion.g
            variants={pauseIconVariants}
            animate={isPlaying ? "visible" : "hidden"}
            style={{ transformOrigin: '40px 40px' }}
          >
            <motion.rect
              x="33"
              y="28"
              width="5"
              height="24"
              fill="white"
              rx="2.5"
              variants={pauseRectVariants}
              custom={0}
              style={{
                transformOrigin: '35.5px 40px',
                filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
              }}
            />
            <motion.rect
              x="42"
              y="28"
              width="5"
              height="24"
              fill="white"
              rx="2.5"
              variants={pauseRectVariants}
              custom={1}
              style={{
                transformOrigin: '44.5px 40px',
                filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
              }}
            />
          </motion.g>
        </motion.svg>
      </motion.button>
    </div>
  );
};

export default MediaPlayerButton;